// Utilities
import { GraphicElementApi } from '@/types/GraphicElement';
import Layer, { GroupLayer } from '@/types/Layer';
import { Save } from '@/types/Save';
import { isLayerDraggable, isThemeLayer } from '@/utils/layer';
import { LayerMapper } from '@/utils/mappers/LayerMapper';
import { defineStore } from 'pinia';
import { Engine } from '@/engine/core/Engine';
const { t } = i18n.global;

export const useStageStore = defineStore('stage', {
    state: () => ({
        drawer: {
            isOpen: false as boolean,
            preventClose: false as boolean,
            expandedView: false as boolean
        },
        currentStep: 0 as number,
        configuration: {
            selectedMethod: null as string | null,
            categoryId: null as string | null,
            productId: null as string | null,
            patternId: null as string | null,
            patternIdByZone: {} as Record<string, string | null>,
            graduationId: null as string | null,
            themeId: null as string | null,
            themeSku: null as string | null,
            view2d: false as boolean,
            showZoneBorders: true as boolean,
            preventCanvasEdition: false as boolean
        },
        isLoading: false as boolean,
        loadingMessage: t('Loading') as string,
        loadingProgress: null as number | null,
        loadingProgressTarget: null as number | null,
        loadingSubMessage: '' as string,
        loadingProgressTimer: null as ReturnType<typeof setInterval> | null,
        showGuides: false as boolean,
        fonts: [] as string[],
        layers: [] as Layer[],
        save: null as Save | null,
        graduationLayer: null as Layer | null,
        patternLayer: null as Layer | null,
        patternLayersByZone: {} as Record<string, Layer>,
        themeLayer: null as GroupLayer | null,
        customerId: null as string | null,
        preventAutoRedirection: false as boolean,
        expectedVariantType: null as string | null,
        selectedZoneId: null as string | null,
        selectedCanvasMeshId: null as string | number | null
    }),
    actions: {
        toggleDrawer() {
            this.drawer.isOpen = !this.drawer.isOpen;
        },
        minimizeDrawer(forceAction: boolean = false) {
            if (this.drawer.preventClose || forceAction) {
                return;
            }
            this.drawer.isOpen = false;
        },
        selectCategory(id: string) {
            if (!id) {
                return;
            }
            this.configuration.categoryId = id;
        },
        selectProduct(id: string) {
            if (!id) {
                return;
            }
            this.configuration.productId = id;
        },
        goToSummary() {
            // @ts-ignore
            this.$router.push({ name: '//products/[productId]/summary' });
        },
        async resetChanges() {
            Engine.getInstance().content = [];
            if (Engine.getInstance().canvas?.canvas) {
                await Engine.getInstance().canvas.reload(Engine.getInstance().content);
            }
            this.updateLayers([]);
        },
        unloadProduct() {
            this.configuration.productId = null;
            this.resetChanges();
        },
        isProductLoaded() {
            return !!this.configuration.productId;
        },
        toggleLoader(isLoading: boolean, message: string = '') {
            this.isLoading = isLoading;
            this.loadingMessage = message;
            if (!isLoading) {
                // Reset complet du mode progression (y compris le timer) quand
                // le loader se ferme, pour ne pas reafficher la derniere valeur
                // au prochain toggleLoader.
                if (this.loadingProgressTimer) {
                    clearInterval(this.loadingProgressTimer);
                    this.loadingProgressTimer = null;
                }
                this.loadingProgress = null;
                this.loadingProgressTarget = null;
                this.loadingSubMessage = '';
            }
        },
        /**
         * Met a jour la cible de progression. Entre deux appels, un timer
         * fait progresser `loadingProgress` doucement vers `target - 1` (on
         * garde 1% de marge pour que le passage au checkpoint suivant soit
         * visible). La vitesse decroit quand on se rapproche de la cible,
         * simulant un travail qui ralentit — evite l'impression de "plante"
         * entre les gros steps (ex: 5% → 40% le temps du print file).
         */
        setLoadingProgress(target: number | null, subMessage: string = '') {
            this.loadingSubMessage = subMessage;

            if (target === null) {
                if (this.loadingProgressTimer) {
                    clearInterval(this.loadingProgressTimer);
                    this.loadingProgressTimer = null;
                }
                this.loadingProgress = null;
                this.loadingProgressTarget = null;
                return;
            }

            this.loadingProgressTarget = target;
            if (this.loadingProgress === null) {
                this.loadingProgress = 0;
            }
            // Permettre un saut immediat si on appelle avec une cible plus
            // petite (rollback/reset) — sinon le tick ne descendra pas.
            if (this.loadingProgress > target) {
                this.loadingProgress = target;
            }

            if (!this.loadingProgressTimer) {
                this.loadingProgressTimer = setInterval(() => {
                    if (this.loadingProgress === null || this.loadingProgressTarget === null) {
                        return;
                    }
                    const cur = this.loadingProgress;
                    const tgt = this.loadingProgressTarget;
                    const cap = Math.max(0, tgt - 1);
                    if (cur >= cap) {
                        return;
                    }
                    // delta proportionnel a la distance restante : rapide au
                    // debut, lent pres de la cible. Plancher a 0.3 pour
                    // toujours bouger de maniere perceptible.
                    const delta = Math.max(0.3, (tgt - cur) * 0.1);
                    this.loadingProgress = Math.min(cur + delta, cap);
                }, 200);
            }
        },
        setSelectedGraduationId(graduationId: string | null) {
            this.configuration.graduationId = graduationId;
        },
        toggleGuides() {
            this.showGuides = !this.showGuides;
        },
        setFonts(fonts: string[]) {
            this.fonts = fonts;
        },
        updateLayers(graphicElementData: GraphicElementApi[]) {
            console.log('🔄 stageStore.updateLayers called:', {
                inputLength: graphicElementData.length,
                inputTypes: graphicElementData.map((e) => e.type)
            });

            const layers: Layer[] = [];
            this.graduationLayer = null;
            this.themeLayer = null;
            const newPatternsByZone: Record<string, Layer> = {};

            graphicElementData.forEach((graphicElement) => {
                const type = graphicElement.type;

                try {
                    if (isLayerDraggable(type)) {
                        const layer = LayerMapper.getLayerFromElement(graphicElement);
                        layers.push(layer);
                    } else if (isGraduationLayer(type)) {
                        this.graduationLayer = LayerMapper.getLayerFromElement(graphicElement);
                    } else if (isPatternLayer(type)) {
                        const zoneId = (graphicElement as any).zoneId || '__global__';
                        newPatternsByZone[zoneId] = LayerMapper.getLayerFromElement(graphicElement);
                    } else if (isThemeLayer(type)) {
                        this.themeLayer = <GroupLayer>LayerMapper.getLayerFromElement(graphicElement);
                    }
                } catch (error) {
                    console.error(`LayerMapper failed for ${type}:`, error);
                }
            });

            this.layers = layers;
            this.patternLayersByZone = newPatternsByZone;

            // Keep patternLayer as the pattern for the currently selected zone
            const currentZone = this.selectedZoneId || '__global__';
            this.patternLayer = newPatternsByZone[currentZone] || null;
        },
        updatePatternLayerForSelectedZone() {
            const currentZone = this.selectedZoneId || '__global__';
            this.patternLayer = this.patternLayersByZone[currentZone] || null;
        },
        updateConfiguration() {
            if (Object.keys(this.patternLayersByZone).length === 0) {
                this.configuration.patternId = null;
                this.configuration.patternIdByZone = {};
            }

            if (!this.themeLayer) {
                this.configuration.themeId = null;
            }

            if (!this.graduationLayer) {
                this.configuration.graduationId = null;
            }
        },
        resetConfigurationForStep(step: number) {
            const commonDefaults = {
                selectedMethod: null,
                patternId: null,
                patternIdByZone: {},
                graduationId: null,
                themeId: null,
                themeSku: null,
                showZoneBorders: true
            };

            const defaultsMap = new Map<number, any>([
                [
                    0,
                    {
                        ...commonDefaults,
                        categoryId: null,
                        productId: null,
                        showZoneBorders: true,
                        preventCanvasEdition: false
                    }
                ],
                [
                    1,
                    {
                        ...commonDefaults,
                        categoryId: null,
                        productId: null,
                        showZoneBorders: true,
                        preventCanvasEdition: false
                    }
                ],
                [
                    2,
                    {
                        ...commonDefaults,
                        showZoneBorders: true
                    }
                ]
            ]);

            Object.assign(this.configuration, defaultsMap.get(step));
        },
        setSelectedZone(zoneId: string | null, canvasMeshId: string | number | null) {
            this.selectedZoneId = zoneId;
            this.selectedCanvasMeshId = canvasMeshId;
            this.updatePatternLayerForSelectedZone();
        }
    }
});
